// src/pages/CandidateJobBoard.tsx: read-only board of active roles for candidates.
import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { supabase } from '../services/supabase';
import { C, Candidate, TalentJob, ASSET_CLASSES, SENIORITIES } from '../lib/talentConnect';
import { card, inputStyle, btnPrimary, Banner, LoadingScreen, Pills } from '../lib/talentUI';

interface JobRow extends TalentJob {
  firm: { id: string; name: string } | null;
}

const ASSET_FILTERS = ['All', ...ASSET_CLASSES];
const SENIORITY_FILTERS: { value: string; label: string }[] = [
  { value: 'all', label: 'All' },
  ...SENIORITIES.map(s => ({ value: s.value as string, label: s.label })),
];

// "2 days ago" style stamp for a posting date.
function relativeDate(ts: string | null): string {
  if (!ts) return '-';
  const days = Math.floor((Date.now() - new Date(ts).getTime()) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years > 1 ? 's' : ''} ago`;
}

function snippet(text: string | null, max = 150): string {
  const t = (text || '').trim();
  if (!t) return '';
  return t.length <= max ? t : `${t.slice(0, max).trimEnd()}…`;
}

export default function CandidateJobBoard() {
  const { user } = useAuth();
  const { profile, loading: profileLoading } = useProfile();

  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [jobs, setJobs]           = useState<JobRow[]>([]);
  // job_id -> connection status, for jobs this candidate has already applied to.
  const [applied, setApplied]     = useState<Record<string, string>>({});
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [busy, setBusy]           = useState<string | null>(null);

  const [assetFilter, setAssetFilter] = useState('All');
  const [seniorityFilter, setSenFilter] = useState('all');
  const [search, setSearch]           = useState('');

  const isCandidate = profile?.role === 'candidate';

  useEffect(() => {
    if (!user || !isCandidate) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      const { data: cand, error: candErr } = await supabase
        .from('candidates').select('*').eq('owner_id', user.id).maybeSingle();
      if (cancelled) return;
      if (candErr) { setError(candErr.message); setLoading(false); return; }
      setCandidate((cand as Candidate) || null);

      const [jobRes, connRes] = await Promise.all([
        supabase.from('talent_jobs').select('*, firm:firms(id, name)')
          .eq('status', 'active').order('created_at', { ascending: false }),
        cand
          ? supabase.from('talent_connections').select('job_id, status').eq('candidate_id', (cand as Candidate).id)
          : Promise.resolve({ data: [], error: null } as any),
      ]);
      if (cancelled) return;
      if (jobRes.error) { setError(jobRes.error.message); setLoading(false); return; }
      setJobs((jobRes.data as unknown as JobRow[]) || []);
      const map: Record<string, string> = {};
      ((connRes.data as { job_id: string | null; status: string }[]) || []).forEach(c => {
        if (c.job_id) map[c.job_id] = c.status;
      });
      setApplied(map);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user, isCandidate]);

  const express = async (job: JobRow) => {
    if (!user || !candidate) return;
    setBusy(job.id); setError('');
    const { error: insErr } = await supabase.from('talent_connections').insert({
      candidate_id: candidate.id,
      manager_firm_id: job.firm_id,
      job_id: job.id,
      requested_by: user.id,
      status: 'pending',
    });
    setBusy(null);
    if (insErr) {
      // 23505 = the unique(job_id, candidate_id, manager_firm_id) constraint:
      // interest already registered, so reflect that rather than erroring.
      if (insErr.code === '23505') { setApplied(prev => ({ ...prev, [job.id]: 'pending' })); return; }
      setError(insErr.message);
      return;
    }
    setApplied(prev => ({ ...prev, [job.id]: 'pending' }));
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return jobs.filter(j => {
      if (assetFilter !== 'All' && !(j.asset_classes || []).includes(assetFilter)) return false;
      if (seniorityFilter !== 'all' && !(j.seniority || []).includes(seniorityFilter)) return false;
      if (q && !`${j.title} ${j.description || ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [jobs, assetFilter, seniorityFilter, search]);

  if (profileLoading) return <LoadingScreen label="Loading…" />;
  if (!isCandidate) return <Navigate to="/dashboard" replace />;

  const pillBtn = (on: boolean): React.CSSProperties => ({
    padding: '5px 12px', borderRadius: '3px', fontSize: '12px', cursor: 'pointer',
    fontFamily: 'system-ui', fontWeight: on ? 600 : 500,
    border: `1px solid ${on ? C.navy : C.border}`,
    background: on ? C.navy : C.white, color: on ? C.white : C.textSec,
  });

  return (
    <div style={{ padding: '28px 32px', minHeight: '100vh', background: C.surface, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ marginBottom: '18px' }}>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: C.navy, fontWeight: 400, margin: '0 0 4px', letterSpacing: '-0.3px' }}>Open Positions</h1>
        <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>Active roles across the alternatives industry</p>
      </div>

      {error && <Banner type="err" text={error} />}
      {!loading && !candidate && (
        <Banner type="info" text="Set up your candidate profile before expressing interest in a role." />
      )}

      {/* ── Filters ── */}
      <div style={{ ...card, padding: '16px 18px', marginBottom: '18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search job title or description…"
          style={{ ...inputStyle, maxWidth: '420px' }}
        />
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px', textTransform: 'uppercase', marginRight: '4px' }}>Asset class</span>
          {ASSET_FILTERS.map(a => (
            <button key={a} onClick={() => setAssetFilter(a)} style={pillBtn(assetFilter === a)}>{a}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px', textTransform: 'uppercase', marginRight: '4px' }}>Seniority</span>
          {SENIORITY_FILTERS.map(s => (
            <button key={s.value} onClick={() => setSenFilter(s.value)} style={pillBtn(seniorityFilter === s.value)}>{s.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ ...card, padding: '48px', textAlign: 'center', fontSize: '13px', color: C.muted }}>Loading roles…</div>
      ) : filtered.length === 0 ? (
        <div style={{ ...card, padding: '48px', textAlign: 'center' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: C.text, marginBottom: '6px' }}>
            {jobs.length === 0 ? 'No open positions right now' : 'No roles match your filters'}
          </div>
          <div style={{ fontSize: '13px', color: C.muted }}>
            {jobs.length === 0 ? 'Check back soon, new roles are posted regularly.' : 'Try clearing a filter or broadening your search.'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))', gap: '14px' }}>
          {filtered.map(job => {
            const status = applied[job.id];
            const isApplied = !!status;
            return (
              <div key={job.id} style={{ ...card, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                    <div style={{ fontFamily: 'Georgia, serif', fontSize: '16px', color: C.navy, lineHeight: 1.35 }}>{job.title}</div>
                    {isApplied && (
                      <span style={{ background: '#F0FDF4', color: C.success, border: '1px solid #BBF7D0', fontSize: '9px', fontWeight: 700, padding: '2px 7px', borderRadius: '2px', letterSpacing: '0.5px', textTransform: 'uppercase', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {status === 'accepted' ? 'Connected' : status === 'declined' ? 'Closed' : 'Applied'}
                      </span>
                    )}
                  </div>
                  {/* Firms without a readable profile come back as null on the embed. */}
                  <div style={{ fontSize: '12.5px', color: job.firm?.name ? C.textSec : C.mutedLt, marginTop: '2px', fontWeight: 500 }}>
                    {job.firm?.name || 'Confidential'}
                  </div>
                </div>

                {((job.asset_classes || []).length > 0 || (job.seniority || []).length > 0) && (
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {(job.asset_classes || []).map(a => (
                      <span key={a} style={{ background: `${C.gold}1A`, color: C.amber, border: `1px solid ${C.gold}55`, fontSize: '10px', fontWeight: 600, padding: '2px 7px', borderRadius: '2px' }}>{a}</span>
                    ))}
                    {(job.seniority || []).map(s => (
                      <span key={s} style={{ background: C.surface, color: C.textSec, border: `1px solid ${C.border}`, fontSize: '10px', fontWeight: 600, padding: '2px 7px', borderRadius: '2px', textTransform: 'capitalize' }}>{s}</span>
                    ))}
                  </div>
                )}

                {(job.location || job.relocation_offered) && (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', fontSize: '12px', color: C.muted }}>
                    {job.location && <span>{job.location}</span>}
                    {job.relocation_offered && (
                      <span style={{ background: `${C.teal}12`, color: C.teal, border: `1px solid ${C.teal}33`, fontSize: '9.5px', fontWeight: 700, padding: '1px 6px', borderRadius: '2px', letterSpacing: '0.4px', textTransform: 'uppercase' }}>Relocation offered</span>
                    )}
                  </div>
                )}

                {job.description && (
                  <p style={{ fontSize: '12.5px', color: C.textSec, lineHeight: 1.6, margin: 0 }}>{snippet(job.description)}</p>
                )}

                {(job.target_roles || []).length > 0 && (
                  <div>
                    <div style={{ fontSize: '9.5px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: '5px' }}>Roles sought</div>
                    <Pills items={job.target_roles || []} max={4} />
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginTop: 'auto', paddingTop: '10px', borderTop: `1px solid ${C.borderLt}` }}>
                  <span style={{ fontSize: '11.5px', color: C.mutedLt }}>Posted {relativeDate(job.created_at)}</span>
                  {isApplied ? (
                    <span style={{ fontSize: '12px', color: C.success, fontWeight: 600 }}>✓ Interest registered</span>
                  ) : (
                    <button
                      onClick={() => express(job)}
                      disabled={busy === job.id || !candidate}
                      title={!candidate ? 'Complete your candidate profile first' : undefined}
                      style={{ ...btnPrimary, opacity: busy === job.id || !candidate ? 0.6 : 1, cursor: !candidate ? 'not-allowed' : 'pointer' }}
                    >{busy === job.id ? 'Sending…' : 'Express Interest'}</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
